import { useEffect, useMemo, useRef, useState } from "react";
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend,
} from "recharts";
import { fmtN } from "../../lib/funnel/helpers";
import { PRIMARY, VIOLET, STEEL, ORANGE, RED, FG1, FG3, MUTED, GRAY, GOLD } from "../../lib/funnel/theme";
import { SectionCardHeader } from "../../components/ChartCard";
import { InfoButton } from "../../lib/funnel/InfoButton";
import type { FilterPillSpec } from "../../components/FilterPills";
import {
  EVENT_TYPES, EVENT_LABEL, ACTIVITY_BUCKETS,
} from "./activitySql";
import type {
  ActivityRaw24h, ActivityDaily, ActivityEventType,
} from "./activitySql";

// Histogram of cohort activity since signup. Two data sources combined:
//   raw24h    — every event in first 24h, lets us bucket by 15m or hour
//   daily90d  — pre-aggregated (user, day, event_type), lets us bucket by day or week
// The user picks bucket + window; we pick the right source under the hood.

type Bucket = "15m" | "hour" | "day" | "week";
type Window = "24h" | "7d" | "30d" | "60d" | "90d";
type YMode = "total" | "per_active_user" | "pct_active";

const BUCKET_LABEL: Record<Bucket, string> = { "15m": "15 min", hour: "Hourly", day: "Daily", week: "Weekly" };
const WINDOW_LABEL: Record<Window, string> = { "24h": "First 24h", "7d": "First 7 days", "30d": "First 30 days", "60d": "First 60 days", "90d": "First 90 days" };

const WINDOW_MINUTES: Record<Window, number> = {
  "24h": 24 * 60,
  "7d": 7 * 24 * 60,
  "30d": 30 * 24 * 60,
  "60d": 60 * 24 * 60,
  "90d": 90 * 24 * 60,
};

// Per-feature colour palette; "all" is teal (PRIMARY).
const SERIES_COLOR: Record<ActivityEventType, string> = {
  transcript: PRIMARY,
  summary: VIOLET,
  custom_template: STEEL,
  calendar: ORANGE,
  chat: RED,
  document: "#7CA8B3",
  task: "#9C7BAA",
  client: "#B58D5A",
  form: GRAY,
  conversion_plus: GOLD,
  conversion_pro: "#22A06B",
};


function bucketKeyMinutes(minutes: number, bucket: Bucket): number {
  switch (bucket) {
    case "15m": return Math.floor(minutes / 15);
    case "hour": return Math.floor(minutes / 60);
    case "day": return Math.floor(minutes / (60 * 24));
    case "week": return Math.floor(minutes / (60 * 24 * 7));
  }
}

function bucketLabelFor(bucket: Bucket, i: number): string {
  switch (bucket) {
    case "15m": {
      const startMin = i * 15;
      const h = Math.floor(startMin / 60);
      const m = startMin % 60;
      return `${h}h${m === 0 ? "" : `:${String(m).padStart(2, "0")}`}`;
    }
    case "hour": return `${i}h`;
    case "day": return `D${i}`;
    case "week": return ACTIVITY_BUCKETS[i]?.label ?? `Bucket ${i + 1}`;
  }
}

export function ActivityHistogram({
  pagePills,
  raw24h,
  daily,
  cohortSize,
  loading,
  compareRaw24h,
  compareDaily,
  compareCohortSize,
  comparePills,
}: {
  pagePills?: FilterPillSpec[];
  raw24h: ActivityRaw24h[];
  daily: ActivityDaily[];
  cohortSize: number;
  loading: boolean;
  compareRaw24h?: ActivityRaw24h[] | null;
  compareDaily?: ActivityDaily[] | null;
  compareCohortSize?: number | null;
  comparePills?: FilterPillSpec[];
}) {
  const compareOn = !!compareRaw24h && !!compareDaily && compareCohortSize != null;
  const [bucket, setBucket] = useState<Bucket>("week");
  const [windw, setWindw] = useState<Window>("30d");
  const [yMode, setYMode] = useState<YMode>("total");
  const [selected, setSelected] = useState<Set<ActivityEventType>>(
    () => new Set(EVENT_TYPES.filter((t) => t !== "conversion_plus" && t !== "conversion_pro")),
  );
  const [seriesMenuOpen, setSeriesMenuOpen] = useState(false);
  const seriesMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!seriesMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (seriesMenuRef.current && !seriesMenuRef.current.contains(e.target as Node)) setSeriesMenuOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [seriesMenuOpen]);

  // 15m/hour buckets MUST use the raw 24h source — daily aggregation can't
  // recover sub-day granularity. Likewise day/week buckets prefer the daily
  // source (cheaper). The window cap also matters: 15m past 24h is meaningless.
  const useRaw = bucket === "15m" || bucket === "hour";
  const effectiveWindowMin = useRaw ? Math.min(WINDOW_MINUTES[windw], WINDOW_MINUTES["24h"]) : WINDOW_MINUTES[windw];

  // Filter both sources to the page-filtered cohort: the parent passes only
  // rows whose user is in the current `users` view, so we trust the inputs.
  function buildBuckets(rawSrc: ActivityRaw24h[], dailySrc: ActivityDaily[], denom: number, keyPrefix: string) {
    const bucketCount = useRaw
      ? Math.ceil(effectiveWindowMin / (bucket === "15m" ? 15 : 60))
      : ACTIVITY_BUCKETS.length;

    type Cell = { total: number; active: Set<string> };
    const cells: Cell[][] = Array.from({ length: bucketCount }, () =>
      EVENT_TYPES.map(() => ({ total: 0, active: new Set<string>() })),
    );
    const evIdx: Record<ActivityEventType, number> = {} as Record<ActivityEventType, number>;
    EVENT_TYPES.forEach((t, i) => { evIdx[t] = i; });

    if (useRaw) {
      for (const r of rawSrc) {
        if (r.minutes_since_signup < 0 || r.minutes_since_signup >= effectiveWindowMin) continue;
        const bi = bucketKeyMinutes(r.minutes_since_signup, bucket);
        if (bi < 0 || bi >= bucketCount) continue;
        const col = cells[bi]?.[evIdx[r.event_type]];
        if (!col) continue;
        col.total += 1;
        col.active.add(r.user_external_id);
      }
    } else {
      for (const d of dailySrc) {
        if (d.bucket_idx < 0 || d.bucket_idx >= bucketCount) continue;
        const col = cells[d.bucket_idx]?.[evIdx[d.event_type]];
        if (!col) continue;
        col.total += d.event_count;
        col.active.add(d.user_external_id);
      }
    }

    return Array.from({ length: bucketCount }, (_, bi) => {
      const row: Record<string, number | string> = { label: bucketLabelFor(bucket, bi) };
      const bucketActive = new Set<string>();
      let total = 0;
      for (const t of EVENT_TYPES) {
        const cell = cells[bi]?.[evIdx[t]];
        if (!cell) continue;
        let v = 0;
        if (yMode === "total") v = cell.total;
        else if (yMode === "per_active_user") v = cell.active.size === 0 ? 0 : cell.total / cell.active.size;
        else v = denom === 0 ? 0 : (cell.active.size / denom) * 100;
        row[`${keyPrefix}${t}`] = v;
        total += v;
        for (const u of cell.active) bucketActive.add(u);
      }
      row[`${keyPrefix}__total`] = total;
      row[`${keyPrefix}__active_users`] = bucketActive.size;
      return row;
    });
  }

  const data = useMemo(() => {
    const base = buildBuckets(raw24h, daily, cohortSize, "");
    if (!compareOn || !compareRaw24h || !compareDaily || compareCohortSize == null) return base;
    const comp = buildBuckets(compareRaw24h, compareDaily, compareCohortSize, "c_");
    return base.map((row, i) => ({ ...row, ...(comp[i] ?? {}) }));
  }, [useRaw, bucket, effectiveWindowMin, raw24h, daily, yMode, cohortSize, compareOn, compareRaw24h, compareDaily, compareCohortSize]);

  const visibleSeries = EVENT_TYPES.filter((t) => selected.has(t));
  const totalEvents = useMemo(() => {
    if (yMode !== "total") return null;
    let sum = 0;
    for (const row of data) {
      for (const t of visibleSeries) sum += Number(row[t] ?? 0);
    }
    return sum;
  }, [data, visibleSeries, yMode]);

  const pills: FilterPillSpec[] = [
    ...(pagePills ?? []),
    ...(compareOn ? [{ kind: "mode" as const, label: "vs comparison" }, ...(comparePills ?? [])] : []),
    { kind: "mode" as const, label: WINDOW_LABEL[windw] },
    { kind: "mode" as const, label: `${BUCKET_LABEL[bucket]} bucket` },
    { kind: "mode" as const, label: yMode === "total" ? "Total events" : yMode === "per_active_user" ? "Per active user" : "% cohort active" },
  ];

  function toggleSeries(t: ActivityEventType) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t); else next.add(t);
      return next;
    });
  }

  return (
    <section className="card">
      <SectionCardHeader
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            Activity histogram by time since signup
            <InfoButton label="How to read this">
              <p style={{ margin: "0 0 6px" }}>
                Each bar is the count of cohort events that fell into that bucket of time since the user signed up.
                Designed to answer: <em>where does activity drop off, and when does it plateau?</em>
              </p>
              <p style={{ margin: "0 0 6px" }}>
                <strong>Bucket</strong> (15m / Hour / Day / Week) sets the bar width. 15m and Hour zoom into the first
                24h to expose the steep onboarding drop; Day / Week show the long tail. <strong>Window</strong>
                clamps the x-axis (24h / 7d / 30d / 60d / 90d).
              </p>
              <p style={{ margin: "0 0 6px" }}>
                <strong>Series</strong> lets you toggle which event types stack into the bars (transcripts,
                summaries, custom templates, calendar, Ask Marloo, documents, tasks, clients, forms). Paid
                conversion events (Plus / Pro) are off by default; toggle them on to see when the cohort upgrades
                relative to first activity.
              </p>
              <p style={{ margin: "0 0 6px" }}>
                <strong>Active user</strong> = a user who had at least one event (of any of the selected series) inside
                that bucket. A user can be active in one bucket and inactive in the next — this is per-bucket, not
                cumulative.
              </p>
              <p style={{ margin: 0, color: FG3 }}>
                <strong>Y-axis</strong>: <em>Total</em> = raw event count in the cohort for that bucket. <em>Per active
                user</em> = events ÷ active users in that bucket (controls for cohort size). <em>% cohort active</em> =
                active users in that bucket ÷ full cohort size in view. Use the same mode when comparing two cohorts
                in an A/B so the y-axes are like-for-like.
              </p>
            </InfoButton>
          </span>
        }
        subtitle={
          loading
            ? "Loading activity stream…"
            : `Where the cohort's events land in the days after sign-up. ${totalEvents !== null ? `${fmtN(totalEvents)} events across ${fmtN(cohortSize)} users in view.` : ""} Switch bucket size to zoom into the early drop-off; switch window to see the long tail.`
        }
        filterPills={pills}
        headerExtra={
          <div style={{ display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <div ref={seriesMenuRef} style={{ position: "relative" }}>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setSeriesMenuOpen((v) => !v)}
                style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
              >
                <i className="ti ti-stack-2" style={{ fontSize: 13 }} />
                Series
                <span style={{ fontSize: 10, fontWeight: 600, color: PRIMARY, background: "rgba(47,122,138,0.12)", padding: "1px 5px", borderRadius: 8 }}>
                  {selected.size}/{EVENT_TYPES.length}
                </span>
                <i className="ti ti-chevron-down" style={{ fontSize: 11, color: FG3 }} />
              </button>
              {seriesMenuOpen && (
                <div
                  role="listbox"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: "absolute", top: "calc(100% + 4px)", right: 0,
                    background: "var(--white)", border: "1px solid var(--border-default)",
                    borderRadius: 6, boxShadow: "var(--shadow-md)", padding: 6, zIndex: 10,
                    minWidth: 220, fontSize: 12,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 6px 6px", borderBottom: "1px solid var(--border-default)" }}>
                    <button type="button" onClick={() => setSelected(new Set(EVENT_TYPES))} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: PRIMARY, fontSize: 11, fontWeight: 500 }}>Select all</button>
                    <button type="button" onClick={() => setSelected(new Set())} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: FG3, fontSize: 11, fontWeight: 500 }}>Clear</button>
                  </div>
                  {EVENT_TYPES.map((t) => {
                    const on = selected.has(t);
                    return (
                      <label key={t} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 6px", cursor: "pointer", borderRadius: 4 }} onClick={(e) => { e.preventDefault(); toggleSeries(t); }}>
                        <span style={{ width: 10, height: 10, background: SERIES_COLOR[t], borderRadius: 2, flexShrink: 0, opacity: on ? 1 : 0.25 }} />
                        <span style={{ color: on ? FG1 : FG3, flex: 1 }}>{EVENT_LABEL[t]}</span>
                        <input type="checkbox" checked={on} onChange={() => {}} tabIndex={-1} style={{ pointerEvents: "none" }} />
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <PillGroup
              value={bucket}
              onChange={setBucket}
              options={[
                { key: "15m", label: "15m" },
                { key: "hour", label: "Hour" },
                { key: "day", label: "Day" },
                { key: "week", label: "Week" },
              ]}
              disabled={(opt) => opt.key === "day"}
            />
            <PillGroup
              value={windw}
              onChange={(v) => setWindw(v as Window)}
              options={[
                { key: "24h", label: "24h" },
                { key: "7d", label: "7d" },
                { key: "30d", label: "30d" },
                { key: "60d", label: "60d" },
                { key: "90d", label: "90d" },
              ]}
              disabled={(opt) => {
                if (useRaw) return opt.key === "30d" || opt.key === "60d" || opt.key === "90d";
                if (bucket === "week") return true;
                return false;
              }}
            />
            <PillGroup
              value={yMode}
              onChange={(v) => setYMode(v as YMode)}
              options={[
                { key: "total", label: "Total" },
                { key: "per_active_user", label: "Per user" },
                { key: "pct_active", label: "% active" },
              ]}
            />
          </div>
        }
      />
      <div className="card-body" style={{ height: 360, position: "relative" }}>
        {data.length === 0 ? (
          <div style={{ color: FG3, fontSize: 12 }}>No data in this window.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 8, right: 16, bottom: 24, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={MUTED} opacity={0.3} vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: FG3 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11, fill: FG3 }} tickFormatter={(v) => yMode === "pct_active" ? `${Math.round(v)}%` : fmtN(v)} />
              <Tooltip
                wrapperStyle={{ zIndex: 50, pointerEvents: "none" }}
                cursor={{ fill: "rgba(47,122,138,0.06)" }}
                content={(props) => {
                  const { active, payload, label } = props as unknown as { active?: boolean; payload?: { dataKey: string; value: number; color: string }[]; label?: string };
                  if (!active || !payload || payload.length === 0) return null;
                  const baseItems = payload.filter((p) => !p.dataKey.startsWith("c_"));
                  const compItems = payload.filter((p) => p.dataKey.startsWith("c_"));
                  const fmtVal = (v: number) => yMode === "pct_active" ? `${v.toFixed(1)}%` : yMode === "per_active_user" ? v.toFixed(2) : fmtN(v);
                  const baseTotal = baseItems.reduce((s, p) => s + Number(p.value ?? 0), 0);
                  const compTotal = compItems.reduce((s, p) => s + Number(p.value ?? 0), 0);
                  return (
                    <div style={{ background: "var(--white)", border: "1px solid var(--border-default)", borderRadius: 7, padding: "8px 10px", fontSize: 12, boxShadow: "var(--shadow-md)", lineHeight: 1.4 }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
                      <div style={{ color: PRIMARY, fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em" }}>Baseline</div>
                      {baseItems.map((p) => (
                        <div key={p.dataKey} style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                          <span style={{ color: p.color }}>● {EVENT_LABEL[p.dataKey as ActivityEventType]}</span>
                          <span style={{ color: FG1, fontWeight: 500 }}>{fmtVal(Number(p.value) || 0)}</span>
                        </div>
                      ))}
                      {yMode === "total" && baseItems.length > 1 && (
                        <div style={{ display: "flex", justifyContent: "space-between", color: FG3, marginTop: 2 }}>
                          <span>Total</span><span style={{ fontWeight: 600 }}>{fmtN(baseTotal)}</span>
                        </div>
                      )}
                      {compItems.length > 0 && (
                        <>
                          <div style={{ color: GOLD, fontWeight: 600, fontSize: 11, marginTop: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Comparison</div>
                          {compItems.map((p) => {
                            const t = p.dataKey.slice(2) as ActivityEventType;
                            return (
                              <div key={p.dataKey} style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                                <span style={{ color: p.color }}>● {EVENT_LABEL[t]}</span>
                                <span style={{ color: FG1, fontWeight: 500 }}>{fmtVal(Number(p.value) || 0)}</span>
                              </div>
                            );
                          })}
                          {yMode === "total" && compItems.length > 1 && (
                            <div style={{ display: "flex", justifyContent: "space-between", color: FG3, marginTop: 2 }}>
                              <span>Total</span><span style={{ fontWeight: 600 }}>{fmtN(compTotal)}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="circle" />
              {visibleSeries.map((t) => (
                <Bar key={t} dataKey={t} name={EVENT_LABEL[t]} stackId="a" fill={SERIES_COLOR[t]} />
              ))}
              {compareOn && visibleSeries.map((t) => (
                <Bar
                  key={`c_${t}`}
                  dataKey={`c_${t}`}
                  name={`${EVENT_LABEL[t]} (cmp)`}
                  legendType="none"
                  stackId="b"
                  fill={SERIES_COLOR[t]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}

function PillGroup<T extends string>({
  value, onChange, options, disabled,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { key: T; label: string }[];
  disabled?: (opt: { key: T; label: string }) => boolean;
}) {
  return (
    <div role="group" style={{ display: "inline-flex", border: `1px solid ${MUTED}`, borderRadius: 6, overflow: "hidden", fontSize: 11 }}>
      {options.map((opt) => {
        const active = value === opt.key;
        const isDisabled = disabled?.(opt) ?? false;
        return (
          <button
            key={opt.key}
            type="button"
            disabled={isDisabled}
            onClick={() => onChange(opt.key)}
            style={{
              padding: "3px 10px",
              background: active ? PRIMARY : "transparent",
              color: isDisabled ? MUTED : active ? "#fff" : FG1,
              border: "none", cursor: isDisabled ? "not-allowed" : "pointer",
              fontWeight: active ? 600 : 400,
              opacity: isDisabled ? 0.5 : 1,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
